import { CSSProperties, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Printer } from 'lucide-react'
import qrcode from 'qrcode-generator'
import { Alerta, Boton, Dialogo } from '../kit'
import { SPA_BASE } from '../../config'
import { Bien } from '../../types/inventario'

/*
  Etiquetas de inventario sobre papel adhesivo precortado (del proyecto anterior,
  honorarios-dcc/src/inventario/etiquetas.jsx).

  La hoja es carta (612 × 792 pt) con 3 columnas y 10 filas: 30 etiquetas. Las cajas
  salen del PDF de cortes que genera la aplicación del papel adhesivo (octubre de 2026):
    · cada etiqueta mide 188,504 × 70,866 pt (66,5 × 25 mm);
    · las columnas empiezan en x = 11,906 / 211,748 / 411,591 pt;
    · la primera fila empieza en y = 41,669 pt del borde superior y cada fila baja 70,866 pt.
  El contenido (5 líneas de Arial 8,04 pt y el QR, styles/app.css `.etq`) va centrado
  en su caja. Todo se mide en pt de CSS (1/72 in), la misma unidad del PDF.
  (Antes las posiciones se tomaban de «Etiquetas inventario DCC.pdf», con otro paso de
  fila y de columna: el desfase crecía hacia abajo y a la derecha.)
*/

export const COLUMNAS = [11.905513, 211.748056, 411.590599]
export const FILAS = 10
export const POR_HOJA = COLUMNAS.length * FILAS
const ANCHO = 188.503959
const ALTO = 70.86615
const PRIMERA_FILA = 41.669296

/**
 * La ficha del bien que abre el QR: `/b/<clave>`, corta para que el QR siga siendo de 33
 * módulos. Pide sesión, pero no un rol. Las etiquetas viejas (`/app/bienes/<id>`) siguen
 * sirviendo a quien ve el inventario.
 */
export const urlBien = (clave: string) => `${window.location.origin}${SPA_BASE}b/${clave}`

/** QR como SVG de módulos cuadrados, con corrección de errores M. */
function QR({ texto }: { texto: string }) {
  const { n, camino } = useMemo(() => {
    const q = qrcode(0, 'M')
    q.addData(texto)
    q.make()
    const modulos = q.getModuleCount()
    let d = ''
    for (let f = 0; f < modulos; f++) for (let c = 0; c < modulos; c++) if (q.isDark(f, c)) d += `M${c} ${f}h1v1h-1z`
    return { n: modulos, camino: d }
  }, [texto])
  return (
    <svg viewBox={`0 0 ${n} ${n}`} shapeRendering="crispEdges" role="img" aria-label={`QR: ${texto}`}>
      <path d={camino} fill="currentColor" />
    </svg>
  )
}

/** Una etiqueta: N°, descripción, serie, marca y modelo, y el QR de la ficha. El texto largo se corta. */
export function Etiqueta({ bien, style }: { bien: Bien; style?: CSSProperties }) {
  const lineas: [string, string][] = [
    ['Inventario:', bien.numero],
    ['Descripción:', bien.descripcion],
    ['Núm. Serie:', bien.serie],
    ['Marca:', bien.marca],
    ['Modelo:', bien.modelo === 'S/M' ? '' : bien.modelo],
  ]
  return (
    <div className="etq" style={style}>
      {lineas.map(([rotulo, valor]) => (
        <div key={rotulo} className="etq__linea">
          <b>{rotulo}</b>
          <span>{valor}</span>
        </div>
      ))}
      <div className="etq__qr">
        <QR texto={urlBien(bien.clave)} />
      </div>
    </div>
  )
}

/** La caja (el corte) del casillero `i` (0 a 29) de la hoja. */
const caja = (i: number): CSSProperties => ({
  left: `${COLUMNAS[i % COLUMNAS.length]}pt`,
  top: `${PRIMERA_FILA + Math.floor(i / COLUMNAS.length) * ALTO}pt`,
  width: `${ANCHO}pt`,
  height: `${ALTO}pt`,
})

/** Las hojas a imprimir. La primera empieza en el casillero `inicio`: los anteriores ya se usaron. */
function HojasEtiquetas({ bienes, inicio }: { bienes: Bien[]; inicio: number }) {
  const hojas: { casillero: number; bien: Bien }[][] = []
  bienes.forEach((bien, i) => {
    const casillero = inicio + i
    ;(hojas[Math.floor(casillero / POR_HOJA)] ??= []).push({ casillero: casillero % POR_HOJA, bien })
  })
  return (
    <div className="hojas-etiquetas" aria-hidden="true">
      {hojas.map((hoja, h) => (
        <div key={h} className="hoja-etq">
          {hoja.map(({ casillero, bien }) => (
            <div key={bien.id} className="etq-caja" style={caja(casillero)}>
              <Etiqueta bien={bien} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Imprimir etiquetas de `bienes`. Se elige el primer casillero libre de la hoja (si ya
 * se despegaron algunas) y se imprime solo eso: la app se oculta mientras se imprime.
 */
export function DialogoEtiquetas({ bienes, onCerrar }: { bienes: Bien[]; onCerrar: () => void }) {
  const [inicio, setInicio] = useState(0)
  const [imprimiendo, setImprimiendo] = useState(false)
  const hojas = Math.ceil((inicio + bienes.length) / POR_HOJA)
  const enLaPrimera = Math.min(bienes.length, POR_HOJA - inicio)

  useEffect(() => {
    if (!imprimiendo) return
    const terminar = () => {
      document.body.classList.remove('imprimiendo-etiquetas')
      setImprimiendo(false)
    }
    document.body.classList.add('imprimiendo-etiquetas')
    window.addEventListener('afterprint', terminar, { once: true })
    const t = setTimeout(() => window.print(), 50)
    return () => {
      clearTimeout(t)
      window.removeEventListener('afterprint', terminar)
      document.body.classList.remove('imprimiendo-etiquetas')
    }
  }, [imprimiendo])

  const titulo = bienes.length === 1 ? `Imprimir la etiqueta de ${bienes[0].numero || bienes[0].descripcion}` : `Imprimir ${bienes.length} etiquetas`
  return (
    <Dialogo abierto onCerrar={onCerrar} titulo={titulo}>
      <div className="pila">
        <p>
          Hoja carta de {POR_HOJA} etiquetas ({COLUMNAS.length} × {FILAS}). Si la hoja ya está usada, elige la primera etiqueta que queda
          por despegar.
        </p>
        <div className="etq-plano-y-texto">
          <div className="etq-plano" role="group" aria-label="Primera etiqueta libre de la hoja">
            {Array.from({ length: POR_HOJA }, (_, i) => {
              const estado = i < inicio ? 'usada' : i < inicio + enLaPrimera ? 'imprime' : 'libre'
              return (
                <button
                  key={i}
                  type="button"
                  className={`etq-plano__casillero etq-plano__casillero--${estado}`}
                  aria-pressed={i === inicio}
                  aria-label={`Empezar en la fila ${Math.floor(i / COLUMNAS.length) + 1}, columna ${(i % COLUMNAS.length) + 1}`}
                  onClick={() => setInicio(i)}
                />
              )
            })}
          </div>
          <div className="pila">
            <p>
              <strong>
                Empieza en la fila {Math.floor(inicio / COLUMNAS.length) + 1}, columna {(inicio % COLUMNAS.length) + 1}
              </strong>
              . {hojas === 1 ? 'Usa 1 hoja.' : `Usa ${hojas} hojas: ${hojas === 2 ? 'la segunda' : 'las demás'} desde la primera etiqueta.`}
            </p>
            <ul className="etq-leyenda texto-secundario">
              <li><span className="etq-plano__casillero etq-plano__casillero--usada" aria-hidden /> Ya despegada</li>
              <li><span className="etq-plano__casillero etq-plano__casillero--imprime" aria-hidden /> Se imprime</li>
              <li><span className="etq-plano__casillero etq-plano__casillero--libre" aria-hidden /> Queda libre</li>
            </ul>
            <div className="etq-marco" title="Así se ve la primera etiqueta">
              <Etiqueta bien={bienes[0]} />
            </div>
          </div>
        </div>
        <Alerta tipo="info">
          Imprime al 100 % (sin «Ajustar a la página») y sin márgenes. La primera vez, prueba en una hoja normal y superponla al papel de
          etiquetas.
        </Alerta>
        <div className="acciones-formulario">
          <Boton variante="text" onClick={onCerrar}>Cerrar</Boton>
          <Boton icono={Printer} onClick={() => setImprimiendo(true)} cargando={imprimiendo}>Imprimir</Boton>
        </div>
      </div>
      {imprimiendo && createPortal(<HojasEtiquetas bienes={bienes} inicio={inicio} />, document.body)}
    </Dialogo>
  )
}
